import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  IndexTable,
  Badge,
  useBreakpoints,
  useIndexResourceState,
  Text,
  Button,
  IndexFilters,
  Tabs,
  useSetIndexFiltersMode,
  Avatar,
  Modal,
  ButtonGroup,
  TextField,
  InlineStack,
  Box
} from '@shopify/polaris';
import useAxiosClient from '../axiosClient';
import { useTranslation } from 'react-i18next';
import '../../i18n';

const TimeOffManagement = () => {
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const { mode, setMode } = useSetIndexFiltersMode();
  const { t } = useTranslation();

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // States
  const [searchValue, setSearchValue] = useState('');
  const [sortSelected, setSortSelected] = useState(['name asc']);
  const [selectedStatusTab, setSelectedStatusTab] = useState(0);
  const [timeOffs, setTimeOffs] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [activeDeleteModal, setActiveDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedTimeOff, setSelectedTimeOff] = useState(null);

  // Fetch time off data
  useEffect(() => {
    const fetchTimeOffs = async () => {
      try {
        const response = await axiosClient.get('/gettimeoffs');
        if (response.data.success) {
          setTimeOffs(response.data.data);
        }
      } catch (error) {
        // Error handled silently
      }
    };
    fetchTimeOffs();
  }, []);

  // Define tabs for status
  const statusTabs = [
    { id: 'all', content: t('all') },
    { id: 'approved', content: t('approved') },
    { id: 'pending', content: t('pending') },
    { id: 'declined', content: t('declined') },
  ];

  // Filtering logic
  let filteredTimeOffs = [...timeOffs];

  // Filter by status
  if (statusTabs[selectedStatusTab]?.id === 'approved') {
    filteredTimeOffs = filteredTimeOffs.filter((t) => t.status === 'Approved');
  } else if (statusTabs[selectedStatusTab]?.id === 'pending') {
    filteredTimeOffs = filteredTimeOffs.filter((t) => t.status === 'Pending');
  } else if (statusTabs[selectedStatusTab]?.id === 'declined') {
    filteredTimeOffs = filteredTimeOffs.filter((t) => t.status === 'Declined');
  }

  // Filter by search value (staff name or policy)
  if (searchValue) {
    filteredTimeOffs = filteredTimeOffs.filter((t) => {
      const staffName = `${t.first_name} ${t.last_name}`.toLowerCase();
      const policy = t.policy_name?.toLowerCase() || '';
      return (
        staffName.includes(searchValue.toLowerCase()) ||
        policy.includes(searchValue.toLowerCase())
      );
    });
  }

  // Sorting logic
  filteredTimeOffs.sort((a, b) => {
    const [sortField, sortDirection] = sortSelected[0].split(' ');
    
    switch (sortField) {
      case 'name':
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return sortDirection === 'asc' 
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      
      case 'date':
        const dateA = new Date(a.start_date);
        const dateB = new Date(b.start_date);
        return sortDirection === 'asc'
          ? dateA - dateB
          : dateB - dateA;
      
      case 'policy':
        const policyA = (a.policy_name || '').toLowerCase();
        const policyB = (b.policy_name || '').toLowerCase();
        return sortDirection === 'asc'
          ? policyA.localeCompare(policyB)
          : policyB.localeCompare(policyA);
      
      case 'status':
        const statusA = (a.status || '').toLowerCase();
        const statusB = (b.status || '').toLowerCase();
        return sortDirection === 'asc'
          ? statusA.localeCompare(statusB)
          : statusB.localeCompare(statusA);
      
      default:
        return 0;
    }
  });

  const resourceName = {
    singular: t('time_off'),
    plural: t('time_offs'),
  };

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  const {
    selectedResources,
    allResourcesSelected,
    handleSelectionChange,
  } = useIndexResourceState(filteredTimeOffs, {
    resourceIDResolver: (timeOff) => timeOff.id?.toString() || '',
  });

  const handleClearAll = useCallback(() => {
    setSearchValue('');
    setSortSelected(['name asc']);
    setSelectedFilters([]);
  }, []);

  // Bulk delete action: open the delete confirmation modal
  const handleBulkDelete = useCallback(() => {
    if (selectedResources.length === 0) {
      shopify.toast.show(t('please_select_at_least_one_time_off_record_to_delete'));
      return;
    }
    setActiveDeleteModal(true);
  }, [selectedResources]);

  // Single item delete action
  const handleSingleDelete = useCallback((timeOff) => {
    setSelectedTimeOff(timeOff);
    setActiveDeleteModal(true);
  }, []);

  // Edit time off action
  const handleEdit = useCallback((timeOffId) => {
    navigate(`/TimeOff/EditTimeOff/${timeOffId}`);
  }, [navigate]);

  const promotedBulkActions = [
    {
      content: t('delete_time_off'),
      onAction: handleBulkDelete,
    },
  ];

  // Confirm delete action
  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      // If we're deleting a single item
      if (selectedTimeOff) {
        const response = await axiosClient.post(`/delete_timeOff/${selectedTimeOff.id}`);
        
        if (response.data.success) {
          setTimeOffs(prevTimeOffs => 
            prevTimeOffs.filter(item => item.id !== selectedTimeOff.id)
          );
          shopify.toast.show(t('timeoff_record_deleted'));
        } else {
          shopify.toast.show(response.data.message || t('failed_to_delete_time_off_record'), { error: true });
        }
      } 
      // If we're bulk deleting
      else if (selectedResources.length > 0) {
        const response = await axiosClient.post('/deleteTimeOffs', {
          ids: selectedResources
        });

        if (response.data.success) {
          setTimeOffs(prevTimeOffs =>
            prevTimeOffs.filter(timeOff => !selectedResources.includes(timeOff.id.toString()))
          );
          handleSelectionChange([]);
          shopify.toast.show(t('time_off_records_deleted'));
        } else {
          shopify.toast.show(response.data.message || t('failed_to_delete_time_off_records'), { error: true });
        }
      }
    } catch (error) {
      shopify.toast.show(error.response?.data?.message || t('error_deleting_time_off_records'), { error: true });
    } finally {
      setIsDeleting(false);
      setActiveDeleteModal(false);
      setSelectedTimeOff(null);
    }
  };

  const [activeRequestId, setActiveRequestId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusUpdateDetails, setStatusUpdateDetails] = useState({
    id: null,
    currentStatus: '',
    newStatus: '',
    notes: ''
  });
  
  // Handle status updates
  const handleStatusUpdate = async (timeOffId, newStatus, notes = '') => {
    try {
      setActiveRequestId(timeOffId);
      setIsLoading(true);

      const timeOff = timeOffs.find(t => t.id === timeOffId);
      if (!timeOff) {
        throw new Error('Time off request not found');
      }
  
      const response = await axiosClient.post(`/update_timeoff_status/${timeOffId}`, {
        status: newStatus,
        notes: notes
      });
  
      if (response.data.success) {
        shopify.toast.show(t('timeoff_request_status', { status: newStatus.toLowerCase() }));
        
        setTimeOffs(prevTimeOffs => 
          prevTimeOffs.map(item => 
            item.id === timeOffId 
              ? { ...item, status: newStatus } 
              : item
          )
        );
      } else {
        const errorMessage = response.data.errors 
          ? Object.values(response.data.errors).flat().join(', ')
          : response.data.message || t('failed_to_update_status');
        shopify.toast.show(errorMessage, { error: true });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(', ')
        : error.response?.data?.message || t('error_updating_time_off_status');
      shopify.toast.show(errorMessage, { error: true });
    } finally {
      setIsLoading(false);
      setActiveRequestId(null);
      setShowStatusModal(false);
    }
  };
  
  // Open the status update modal
  const openStatusModal = (timeOff, newStatus) => {
    setStatusUpdateDetails({
      id: timeOff.id,
      currentStatus: timeOff.status,
      newStatus: newStatus,
      notes: ''
    });
    setShowStatusModal(true);
  };

  return (
    <Page title={t('time_off_management')} fullWidth>
      <Layout>
        <Layout.Section>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '5px', alignItems: 'center', marginBottom: '1rem' }}>
            <Button onClick={() => navigate('/TimeOff/Policy')}>
              {t('view_policy')}
            </Button>
            <Button variant="primary" onClick={() => navigate('/TimeOff/CreateTimeOff')}>
              {t('create_time_off')}
            </Button>
          </div>

          {/* Status Update Modal */}
          <Modal
            open={showStatusModal}
            onClose={() => setShowStatusModal(false)}
            title={t('status_time_off_request', { status: statusUpdateDetails.newStatus })}
            primaryAction={{
              content: t('confirm'),
              onAction: () => {
                handleStatusUpdate(
                  statusUpdateDetails.id,
                  statusUpdateDetails.newStatus,
                  statusUpdateDetails.notes
                );
              }
            }}
            secondaryActions={[
              {
                content: t('cancel'),
                onAction: () => setShowStatusModal(false)
              }
            ]}
          >
            <Modal.Section>
              <Text variant="bodyMd">
                {t('are_you_sure_you_want_to_status_time_off_request', { status: statusUpdateDetails.newStatus.toLowerCase() })}
              </Text>
              <div style={{ marginTop: '16px' }}>
                <TextField
                  label={t('notes_optional')}
                  value={statusUpdateDetails.notes}
                  onChange={(value) => setStatusUpdateDetails(prev => ({ ...prev, notes: value }))}
                  multiline={3}
                  placeholder={t('add_any_notes_about_this_decision')}
                />
              </div>
            </Modal.Section>
          </Modal>

          <Card>
            <IndexFilters
              sortOptions={[
                {label: t('name'), value: 'name asc', directionLabel: t('a_z')},
                {label: t('name'), value: 'name desc', directionLabel: t('z_a')},
                {label: t('date'), value: 'date asc', directionLabel: t('earliest_first')},
                {label: t('date'), value: 'date desc', directionLabel: t('latest_first')},
                {label: t('policy'), value: 'policy asc', directionLabel: t('a_z')},
                {label: t('policy'), value: 'policy desc', directionLabel: t('z_a')},
                {label: t('status'), value: 'status asc', directionLabel: t('a_z')},
                {label: t('status'), value: 'status desc', directionLabel: t('z_a')},
              ]}
              sortSelected={sortSelected}
              queryValue={searchValue}
              queryPlaceholder={t('search_time_offs')}
              onQueryChange={handleSearchChange}
              onQueryClear={() => setSearchValue('')}
              onSort={(value) => setSortSelected(value)}
              cancelAction={{
                onAction: handleClearAll,
                disabled: false,
              }}
              tabs={statusTabs}
              selected={selectedStatusTab}
              onSelect={setSelectedStatusTab}
              filters={[]}
              appliedFilters={selectedFilters}
              onClearAll={handleClearAll}
              mode={mode}
              setMode={setMode}
              loading={false}
              canCreateNewView={false}
            />

            <IndexTable
              resourceName={resourceName}
              itemCount={filteredTimeOffs.length}
              selectedItemsCount={allResourcesSelected ? t('all') : selectedResources.length}
              onSelectionChange={handleSelectionChange}
              headings={[
                { title: t('staff_name') },
                { title: t('policy') },
                { title: t('date') },
                { title: t('reason') },
                { title: t('status') },
                { title: t('by') },
                { title: t('action') },
              ]}
              selectable
              promotedBulkActions={promotedBulkActions}
            >
              {filteredTimeOffs.map((timeOff, index) => (
                <IndexTable.Row
                  id={timeOff.id?.toString() || index.toString()}
                  key={timeOff.id?.toString() || index.toString()}
                  position={index}
                  selected={selectedResources.includes(timeOff.id?.toString())}
                  onClick={() => handleEdit(timeOff.id)}
                >
                  <IndexTable.Cell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Avatar
                        name={`${timeOff.first_name} ${timeOff.last_name}`}
                        initials={getInitials(timeOff.first_name, timeOff.last_name)}
                      />
                      <div>
                        <Text variant="bodyMd" fontWeight="bold">
                          {`${timeOff.first_name} ${timeOff.last_name}`}
                        </Text>
                      </div>
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>{timeOff.policy_name || t('n_a')}</IndexTable.Cell>
                  <IndexTable.Cell>
                    {timeOff.start_date === timeOff.end_date
                      ? timeOff.start_date
                      : `${timeOff.start_date} - ${timeOff.end_date}`}
                  </IndexTable.Cell>
                  <IndexTable.Cell>{timeOff.reason || t('n_a')}</IndexTable.Cell>
                  <IndexTable.Cell>
                    <Badge
                      tone={
                        timeOff.status === 'Approved'
                          ? 'success'
                          : timeOff.status === 'Declined'
                          ? 'critical'
                          : timeOff.status === 'Pending'
                          ? 'warning'
                          : 'info'
                      }
                    >
                      {timeOff.status || t('n_a')}
                    </Badge>
                  </IndexTable.Cell>
                  <IndexTable.Cell>{timeOff.mark_by || t('n_a')}</IndexTable.Cell>
                  <IndexTable.Cell>
                    <div style={{ display: 'flex', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
                      <Button variant="primary" loading={isLoading && activeRequestId === timeOff.id}
                        disabled={isLoading || timeOff.status === 'Approved'} onClick={() => openStatusModal(timeOff, 'Approved')}>{t('approve')}</Button>
                      <Button variant="primary" tone="critical" disabled={isLoading || timeOff.status === 'Declined'} loading={isLoading && activeRequestId === timeOff.id} onClick={() => openStatusModal(timeOff, 'Declined')}>{t('decline')}</Button>
                    </div>
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          </Card>

          {/* Delete Confirmation Modal */}
          <Modal
            open={activeDeleteModal}
            onClose={() => {
              setActiveDeleteModal(false);
              setSelectedTimeOff(null);
            }}
            title={selectedTimeOff ? t('delete_time_off_record') : t('delete_time_off_records')}
            primaryAction={{
              content: t('delete'),
              destructive: true,
              onAction: handleConfirmDelete,
              loading: isDeleting,
            }}
            secondaryActions={[
              {
                content: t('cancel'),
                onAction: () => {
                  setActiveDeleteModal(false);
                  setSelectedTimeOff(null);
                },
              },
            ]}
          >
            <Modal.Section>
              <Text variant="bodyMd">
                {selectedTimeOff 
                  ? t('are_you_sure_you_want_to_delete_time_off_record_for_staff', { staffName: `${selectedTimeOff.first_name} ${selectedTimeOff.last_name}` })
                  : t('are_you_sure_you_want_to_delete_selected_time_off_records')}
              </Text>
              <Text variant="bodyMd" tone="critical" as="p" fontWeight="medium" style={{ marginTop: '8px' }}>
                {t('this_action_cannot_be_undone')}
              </Text>
            </Modal.Section>
          </Modal>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default TimeOffManagement;